// @ts-check

import { useInfiniteQuery, useMutation } from "@tanstack/react-query"
import { atom, useAtom, useAtomValue, useSetAtom } from "jotai"
import { useSelector } from "react-redux"
import { ChatMessageMutationOptions, ChatMessageQueryOptions } from "./query"
import { useActiveChatSessionId, useCreateChatSession, useIsAwaitingNewSession } from "../sessions"
import { useGetChatSessionStream } from "../streams"
import { activeStreamsAtom, ActiveStreamsAtomActions, handleAgentStreamEvent, useStreamMutation } from "../streams"
import { clearReviewProgress, startReviewProgress } from "../canvas/reviewProgress"
import { extractFileContents, StreamEvents } from "../api"
import {
  ChatStreamContentChunkSchema,
  ChatStreamStructuredContentChunkSchema,
  AgentChatChunkDataContentSchema
} from "../schema"
import { CancelledPromiseError } from "@minohealth/common/utils/cancellable-promise"
import { isUserWhitelistedForModelParamsControls } from "@/components/model-params-controls"
import z from "zod"
import { store } from "@/lib/store"
import { AGENT_USER, AgentMode, AI_USER, DEEP_RESEARCH_USER, STARTING_MESSAGE_EDIT_EVENT, useGetLatestUserToken } from "../utils"
import useSharedEffect from "@/hooks/useSharedEffect"

const modeAtom = atom(/**@type{import('../utils').AgentModeValue}*/(AgentMode.AGENT))

const modelConfigAtom = atom({
  /** @type {number | undefined} */
  temperature: undefined,
  /** @type {string | undefined} */
  systemPrompt: undefined,
  /** @type {number | undefined} */
  topP: undefined,
})

const inputMessageAtom = atom({
  text: "",
  /** @type {ChatFile[]} */
  files: [],
  /** @type {ChatImage[]} */
  images: [],
})

const pendingMessageAtom = atom(/**@type{{text: string, files?: ChatFile[], images?: ChatImage[], voiceChatSystemPrompt?: string} | null}*/(null))
const pendingAgentMessageAtom = atom(/**@type{{query: string, images?: ChatImage[], mode: import("../utils").AgentModeValue, forceAgentMode?: import("../utils").AgentModeValue}}*/(null))

/** @typedef {typeof inputMessageAtom} InputMessageAtom */
/** @typedef {typeof inputMessageAtom extends import("jotai").PrimitiveAtom<infer T> ? T : never} InputMessage */

function useAgentMode() {
  return useAtom(modeAtom)
}

function useModelConfiguration() {
  const { user } = useSelector(state => state.app.datastate)
  const modelConfigStateTuple = useAtom(modelConfigAtom)

  if(!isUserWhitelistedForModelParamsControls(user?.email)) {
    return /**@type{typeof modelConfigStateTuple}*/(/**@type{unknown}*/([{}, () => {}]))
  }

  return modelConfigStateTuple
}

function useInputMessage() {
  return useAtom(inputMessageAtom)
}

function usePendingMessage() {
  return useAtom(pendingMessageAtom)
}

function usePendingAgentMessage() {
  return useAtom(pendingAgentMessageAtom)
}

/** @param {InputMessage} message */
function revertSentMessage(message) {
  store.set(inputMessageAtom, prev => ({
    text: message.text || prev.text,
    files: message.files?.length ? message.files : prev.files,
    images: message.images?.length ? message.images : prev.images,
  }))
}

const PendingMessageEffectSymbol = Symbol("PendingMessageEffect")
function useSendChatMessage() {
  const { user } = useSelector((state) => state.app.datastate)
  const [activeSessionId] = useActiveChatSessionId()
  const { temperature, systemPrompt, topP } = useAtomValue(modelConfigAtom)
  const setActiveStreams = useSetAtom(activeStreamsAtom)
  const getUserToken = useGetLatestUserToken()

  const createChatSessionMutation = useCreateChatSession()

  const [pendingMessage, setPendingMessage] = useAtom(pendingMessageAtom)
  const [isAwaitingNewSession, setIsAwaitingNewSession] = useIsAwaitingNewSession()

  useSharedEffect(PendingMessageEffectSymbol, () => {
    if(isAwaitingNewSession) return
    if(pendingMessage == null) return
    if(activeSessionId == null) return

    sendChatMessageMutation.mutate({ ...pendingMessage, sessionId: activeSessionId })
    setPendingMessage(null)
  }, [pendingMessage, isAwaitingNewSession, activeSessionId])

  /** @param {import("../api").StreamEventsPayload} payload */
  function handleStreamEvent(payload) {
    switch(payload.event) {
      case StreamEvents.STREAM_STARTED: {
        break;
      }
      case StreamEvents.STREAM_CONTENT: {
        let chunk
        try {
          chunk = ChatStreamContentChunkSchema.parse(payload.data.chunk)
        } catch (error) {
          console.warn("Failed to parse chat stream content event data", error)
          return
        }

        setActiveStreams(
          ActiveStreamsAtomActions.APPEND_RESPONSE_CHUNK(
            payload.data.sessionId,
            "content",
            chunk.data.content
          )
        )
        break;
      }
      case StreamEvents.STREAM_STRUCTURED_CONTENT: {
        console.warn("Unexpected event for chat", payload)
        return
      }
      case StreamEvents.STREAM_ENDED: {
        break;
      }
      default:
        break;
    }
  }

  const mutationOptions = ChatMessageMutationOptions.SEND_MESSAGE({
    user,
    country: "",
    temperature,
    systemPrompt,
    topP,
    getUserToken,
    onStreamEvent: handleStreamEvent,
  })

  const { mutate: getSessionStream } = useGetChatSessionStream()

  const sendChatMessageMutation = useStreamMutation(mutationOptions, {
    mutationFn: async (variables, context) => {
      if(variables.sessionId == null) {
        setPendingMessage(variables)
        setIsAwaitingNewSession(true)

        await createChatSessionMutation.mutateAsync().catch(error => {
          setPendingMessage(null)
          revertSentMessage({
            text: variables.text,
            images: variables.images,
            files: variables.files,
          })
          throw error
        }).finally(() => {
          setIsAwaitingNewSession(false)
        })

        const abortController = new AbortController()
        abortController.abort()
        abortController.signal.throwIfAborted()
      }

      /** @type {ChatImage[]} */
      let resolvedImages = [...(variables.images ?? [])]
      /** @type {ChatFile[]} */
      let resolvedFiles = [...(variables.files ?? [])]

      // TODO: Handle cancel error
      const uploadPromises = [
        ...(variables.images ?? []).map((img, i) => {
          if (img.resourceUrl != null || img.uploadPromise == null) return null
          return img.uploadPromise.then(uploaded => {
            resolvedImages[i] = Object.assign({}, img, uploaded)
            // @ts-ignore
            setActiveStreams(prev => {
              if (prev[variables.sessionId] == null) return prev
              return {
                ...prev,
                [variables.sessionId]: {
                  ...prev[variables.sessionId],
                  query: {
                    ...prev[variables.sessionId].query,
                    images: resolvedImages,
                  }
                }
              }
            })
          }).catch(error => {
            if (error === CancelledPromiseError) return
            throw new Error(error.message ?? "Failed to upload image")
          })
        }),
        ...(variables.files ?? []).map((file, i) => {
          if (file.content != null || file.uploadPromise == null) return null
          return file.uploadPromise.then(content => {
            resolvedFiles[i] = Object.assign({}, file, { content })
            // @ts-ignore
            setActiveStreams(prev => {
              if (prev[variables.sessionId] == null) return prev
              return {
                ...prev,
                [variables.sessionId]: {
                  ...prev[variables.sessionId],
                  query: {
                    ...prev[variables.sessionId].query,
                    files: resolvedFiles,
                  }
                }
              }
            })
          }).catch(error => {
            if (error === CancelledPromiseError) return
            throw new Error(error.message ?? "Unsupported document type")
          })
        }),
      ].filter(Boolean)

      if(uploadPromises.length != 0) {
        await Promise.allSettled(uploadPromises).then(results => {
          if(results.length > 1 && results.every(res => res.status === "rejected")) {
            throw new Error("Failed to upload files")
          }

          for(const result of results) {
            if(result.status === "fulfilled") continue
            throw new Error(result.reason)
          }
        })
      }

      return mutationOptions.mutationFn({
        ...variables,
        images: resolvedImages,
        files: resolvedFiles,
      }, context)
    },
    onMutate(variables, context) {
      if(variables.sessionId == null) return

      clearReviewProgress(variables.sessionId)
      setActiveStreams(
        ActiveStreamsAtomActions.SETUP_CHAT_STREAM(variables.sessionId, {
          message: variables.text,
          images: variables.images,
          files: variables.files,
        }, "content")
      )
    },
    onError: (error, variables, mutationResult, context) => {
      if(error.name === "AbortError") {
        sendChatMessageMutation.reset()
        return
      }

      revertSentMessage({
        text: variables.text,
        images: variables.images,
        files: variables.files,
      })
      // TODO: Invalidate chat query if error is NOT_FOUND(Stream finished on the server)
    },
    onSettled: (data, error, variables) => {
      setActiveStreams(ActiveStreamsAtomActions.DELETE_STREAM(variables.sessionId))
    }
  }, {
    getSessionStream,
    sessionId: activeSessionId
  })

  return sendChatMessageMutation
}

function useEditChatMessage() {
  const { user } = useSelector((state) => state.app.datastate)
  const [activeSessionId] = useActiveChatSessionId()
  const { temperature, systemPrompt, topP } = useAtomValue(modelConfigAtom)
  const setActiveStreams = useSetAtom(activeStreamsAtom)
  const getUserToken = useGetLatestUserToken()

  /** @param {import("../api").StreamEventsPayload} payload */
  function handleStreamEvent(payload) {
    switch(payload.event) {
      case StreamEvents.STREAM_STARTED: {
        break;
      }
      case StreamEvents.STREAM_CONTENT: {
        let chunk
        try {
          chunk = ChatStreamContentChunkSchema.parse(payload.data.chunk)
        } catch (error) {
          console.warn("Failed to parse edit message stream content event data", error)
          return
        }

        setActiveStreams(
          ActiveStreamsAtomActions.APPEND_RESPONSE_CHUNK(
            payload.data.sessionId,
            "content",
            chunk.data.content
          )
        )
        break;
      }
      case StreamEvents.STREAM_STRUCTURED_CONTENT: {
        let chunk
        try {
          chunk = ChatStreamStructuredContentChunkSchema.parse(payload.data.chunk)
        } catch (error) {
          console.warn("Failed to parse edit message stream structured content event data", error)
          return
        }

        let eventData
        try {
          eventData = AgentChatChunkDataContentSchema.parse(chunk.data.content)
        } catch (error) {
          console.warn("Failed to parse edit message stream structured content event data", error)
          return
        }

        handleAgentStreamEvent(eventData, payload.data.sessionId)
        break;
      }
      case StreamEvents.STREAM_ENDED: {
        break;
      }
      default:
        break;
    }
  }

  const mutationOptions = ChatMessageMutationOptions.EDIT_MESSAGE({
    sessionId: activeSessionId,
    user,
    country: "",
    temperature,
    systemPrompt,
    topP,
    getUserToken,
    onStreamEvent: handleStreamEvent,
  })

  const { mutate: getSessionStream } = useGetChatSessionStream()

  return useStreamMutation(mutationOptions, {
    onMutate(variables, context, result) {
      if(activeSessionId == null) return

      clearReviewProgress(activeSessionId)
      const editedMessageIndex = result.removedMessages.findIndex(message => message._id === variables.messageId)
      const editedMessage = editedMessageIndex !== -1 ? result.removedMessages[editedMessageIndex] : null
      if(editedMessage == null) return result

      const editedMessageResponse = result.removedMessages[editedMessageIndex + 1]

      const responseContentType = (() => {
        if(editedMessageResponse == null) return "content"
        if(editedMessageResponse?.user !== AI_USER) return "structured-content"
        return "content"
      })()

      const editedMessageMode = (() => {
        if(editedMessageResponse == null) return AgentMode.DEFAULT
        if(editedMessageResponse?.user === AGENT_USER) return AgentMode.AGENT
        if(editedMessageResponse?.user === DEEP_RESEARCH_USER) return AgentMode.DEEP_RESEARCH
        return AgentMode.DEFAULT
      })()

      setActiveStreams(
        ActiveStreamsAtomActions.SETUP_CHAT_STREAM(activeSessionId, {
          message: variables.text,
          images: editedMessage.images,
          files: editedMessage.files,
          mode: editedMessageMode,
        }, responseContentType)
      )
    },
    onError: (error, variables, mutationResult, context) => {
      if(error.name === "AbortError") return

      document.dispatchEvent(
        new CustomEvent(STARTING_MESSAGE_EDIT_EVENT, {
          detail: { messageId: variables.messageId }
        })
      )
    },
    onSettled: (data) => {
      setActiveStreams(ActiveStreamsAtomActions.DELETE_STREAM(activeSessionId))
    }
  }, {
    getSessionStream,
    sessionId: activeSessionId
  })
}

function useRegenerateChatMessage() {
  const { user } = useSelector((state) => state.app.datastate)
  const [activeSessionId] = useActiveChatSessionId()
  const { temperature, systemPrompt, topP } = useAtomValue(modelConfigAtom)
  const setActiveStreams = useSetAtom(activeStreamsAtom)
  const getUserToken = useGetLatestUserToken()

  /** @param {import("../api").StreamEventsPayload} payload */
  function handleStreamEvent(payload) {
    switch(payload.event) {
      case StreamEvents.STREAM_STARTED: {
        break;
      }
      case StreamEvents.STREAM_CONTENT: {
        let chunk
        try {
          chunk = ChatStreamContentChunkSchema.parse(payload.data.chunk)
        } catch (error) {
          console.warn("Failed to parse regenerate message stream content event data", error)
          return
        }

        setActiveStreams(
          ActiveStreamsAtomActions.APPEND_RESPONSE_CHUNK(
            payload.data.sessionId,
            "content",
            chunk.data.content
          )
        )
        break;
      }
      case StreamEvents.STREAM_STRUCTURED_CONTENT: {
        let chunk
        try {
          chunk = ChatStreamStructuredContentChunkSchema.parse(payload.data.chunk)
        } catch (error) {
          console.warn("Failed to parse regenerate message stream structured content event data", error)
          return
        }

        let eventData
        try {
          eventData = AgentChatChunkDataContentSchema.parse(chunk.data.content)
        } catch (error) {
          console.warn("Failed to parse regenerate message stream structured content event data", error)
          return
        }

        handleAgentStreamEvent(eventData, payload.data.sessionId)
        break;
      }
      case StreamEvents.STREAM_ENDED: {
        break;
      }
      default:
        break;
    }
  }

  const mutationOptions = ChatMessageMutationOptions.REGENERATE_MESSAGE({
    sessionId: activeSessionId,
    user,
    country: "",
    temperature,
    systemPrompt,
    topP,
    getUserToken,
    onStreamEvent: handleStreamEvent,
  })

  const { mutate: getSessionStream } = useGetChatSessionStream()

  return useStreamMutation(mutationOptions, {
    onMutate(variables, context, result) {
      if(activeSessionId == null) return

      clearReviewProgress(activeSessionId)
      const regeneratedMessage = result.removedMessages.find(message => message._id === variables.messageId)
      const responseContentType = (() => {
        if(regeneratedMessage == null) return "content"
        if(regeneratedMessage?.user !== AI_USER) return "structured-content"
        return "content"
      })()

      const regeneratedMessageMode = (() => {
        if(regeneratedMessage == null) return AgentMode.DEFAULT
        if(regeneratedMessage?.user === AGENT_USER) return AgentMode.AGENT
        if(regeneratedMessage?.user === DEEP_RESEARCH_USER) return AgentMode.DEEP_RESEARCH
        return AgentMode.DEFAULT
      })()

      setActiveStreams(
        ActiveStreamsAtomActions.SETUP_CHAT_STREAM(
          activeSessionId,
          { mode: regeneratedMessageMode },
          responseContentType,
        )
      )
    },
    onError: (error, variables, mutationResult, context) => {
      if(error.name === "AbortError") return
    },
    onSettled: (data) => {
      setActiveStreams(ActiveStreamsAtomActions.DELETE_STREAM(activeSessionId))
    }
  }, {
    getSessionStream,
    sessionId: activeSessionId
  })
}

const PendingAgentMessageEffectSymbol = Symbol("PendingAgentMessageEffect")
function useSendAgentMessage() {
  const { user } = useSelector((state) => state.app.datastate)
  const [activeSessionId] = useActiveChatSessionId()
  const { temperature, systemPrompt, topP } = useAtomValue(modelConfigAtom)
  const setActiveStreams = useSetAtom(activeStreamsAtom)
  const [mode, setMode] = useAgentMode()
  const getUserToken = useGetLatestUserToken()

  const isAgentMode = mode !== AgentMode.DEFAULT
  const [pendingMessage, setPendingMessage] = useAtom(pendingAgentMessageAtom)
  const [isAwaitingNewSession, setIsAwaitingNewSession] = useIsAwaitingNewSession()

  const createChatSessionMutation = useCreateChatSession()

  useSharedEffect(PendingAgentMessageEffectSymbol, () => {
    if(isAwaitingNewSession) return
    if(pendingMessage == null) return
    if(activeSessionId == null) return

    sendAgentMessageMutation.mutate({ ...pendingMessage, sessionId: activeSessionId })
    setPendingMessage(null)
  }, [pendingMessage, isAwaitingNewSession, activeSessionId])

  /** @param {import("../api").StreamEventsPayload} payload */
  function handleStreamEvent(payload) {
    switch(payload.event) {
      case StreamEvents.STREAM_STARTED: {
        break;
      }
      case StreamEvents.STREAM_CONTENT: {
        console.warn("Unexpected event for agent chat", payload)
        return
      }
      case StreamEvents.STREAM_STRUCTURED_CONTENT: {
        let chunk
        try {
          chunk = ChatStreamStructuredContentChunkSchema.parse(payload.data.chunk)
        } catch (error) {
          console.warn("Failed to parse agent message stream structured content event data", error)
          return
        }

        let eventData
        try {
          eventData = AgentChatChunkDataContentSchema.parse(chunk.data.content)
        } catch (error) {
          console.warn("Failed to parse agent message stream structured content event data", error)
          return
        }

        handleAgentStreamEvent(eventData, payload.data.sessionId)
        break;
      }
      case StreamEvents.STREAM_ENDED: {
        break;
      }
      default:
        break;
    }
  }

  const mutationOptions = ChatMessageMutationOptions.SEND_AGENT_MESSAGE({
    user,
    country: "",
    temperature,
    systemPrompt,
    topP,
    getUserToken,
    onStreamEvent: handleStreamEvent,
  })

  const { mutate: getSessionStream } = useGetChatSessionStream()

  const sendAgentMessageMutation = useStreamMutation(mutationOptions, {
    mutationFn: async (variables, context) => {
      if(!isAgentMode && variables.forceAgentMode == null) {
        throw new Error("Agent mode is not enabled")
      }

      if(variables.forceAgentMode) {
        setMode(variables.forceAgentMode)
      }

      if(variables.sessionId == null) {
        setPendingMessage(variables)
        setIsAwaitingNewSession(true)

        await createChatSessionMutation.mutateAsync().catch(error => {
          setPendingMessage(null)
          throw error
        }).finally(() => {
          setIsAwaitingNewSession(false)
        })

        const abortController = new AbortController()
        abortController.abort()
        abortController.signal.throwIfAborted()
      }

      /** @type {ChatImage[]} */
      let resolvedImages = [...(variables.images ?? [])]
      /** @type {ChatFile[]} */
      let resolvedFiles = [...(variables.files ?? [])]

      const uploadPromises = [
        ...(variables.images ?? []).map((img, i) => {
          if (img.resourceUrl != null || img.uploadPromise == null) return null
          return img.uploadPromise.then(uploaded => {
            resolvedImages[i] = Object.assign({}, img, uploaded)
            // @ts-ignore
            setActiveStreams(prev => {
              if (prev[variables.sessionId] == null) return prev
              return {
                ...prev,
                [variables.sessionId]: {
                  ...prev[variables.sessionId],
                  query: {
                    ...prev[variables.sessionId].query,
                    images: resolvedImages,
                  }
                }
              }
            })
          }).catch(error => {
            if (error === CancelledPromiseError) return
            throw new Error(error.message ?? "Failed to upload image")
          })
        }),
        ...(variables.files ?? []).map((file, i) => {
          if (file.content != null || file.uploadPromise == null) return null
          return file.uploadPromise.then(content => {
            resolvedFiles[i] = Object.assign({}, file, { content })
            // @ts-ignore
            setActiveStreams(prev => {
              if (prev[variables.sessionId] == null) return prev
              return {
                ...prev,
                [variables.sessionId]: {
                  ...prev[variables.sessionId],
                  query: {
                    ...prev[variables.sessionId].query,
                    files: resolvedFiles,
                  }
                }
              }
            })
          }).catch(error => {
            if (error === CancelledPromiseError) return
            throw new Error(error.message ?? "Unsupported document type")
          })
        }),
      ].filter(Boolean)

      if(uploadPromises.length != 0) {
        await Promise.allSettled(uploadPromises).then(results => {
          for(const result of results) {
            if(result.status === "fulfilled") continue
            throw new Error(result.reason)
          }
        })
      }

      return mutationOptions.mutationFn({
        ...variables,
        images: resolvedImages,
        files: resolvedFiles,
      }, context)
    },
    onMutate(variables, context) {
      if(variables.sessionId == null) return

      clearReviewProgress(variables.sessionId)
      if(variables.isReview) startReviewProgress(variables.sessionId)

      setActiveStreams(
        ActiveStreamsAtomActions.SETUP_CHAT_STREAM(variables.sessionId, {
          message: variables.query,
          images: variables.images,
          files: variables.files,
          mode: variables.mode,
        }, "structured-content")
      )
    },
    onError: (error, variables, mutationResult, context) => {
      if(error.name === "AbortError") {
        sendAgentMessageMutation.reset()
        return
      }

      revertSentMessage({
        text: variables.query,
        images: variables.images ?? [],
        files: variables.files ?? [],
      })
    },
    onSettled: (data, error, variables) => {
      setActiveStreams(ActiveStreamsAtomActions.DELETE_STREAM(variables.sessionId))
    }
  }, {
    getSessionStream,
    sessionId: activeSessionId
  })

  return sendAgentMessageMutation
}

function useSendChatFeedback() {
  const { user } = useSelector((state) => state.app.datastate)
  const [activeSessionId] = useActiveChatSessionId()

  return useMutation(
    ChatMessageMutationOptions.SEND_FEEDBACK({
      sessionId: activeSessionId,
      userToken: user?.userToken
    })
  )
}

function useUploadFile() {
  const { user } = useSelector((state) => state.app.datastate)

  return useMutation({
    /** @param {{ file: File }} */
    mutationFn: ({ file }) => (
      extractFileContents(file, user?.userToken).then(data => data.content)
    )
  })
}

function useActiveChatMessages() {
  // @ts-ignore
  const { user } = useSelector((state) => state.app.datastate)
  const [activeSessionId] = useActiveChatSessionId()

  return useInfiniteQuery(
    ChatMessageQueryOptions.CHAT_MESSAGES({
      sessionId: activeSessionId,
      userToken: user?.userToken,
    })
  )
}

export * from "./query"
export {
  modeAtom,
  modelConfigAtom,
  inputMessageAtom,
  pendingMessageAtom,
  pendingAgentMessageAtom,

  useAgentMode,
  useModelConfiguration,
  useInputMessage,
  usePendingMessage,
  usePendingAgentMessage,

  revertSentMessage,

  useSendChatMessage,
  useEditChatMessage,
  useRegenerateChatMessage,
  useSendAgentMessage,
  useSendChatFeedback,
  useUploadFile,
  useActiveChatMessages,
}
